import React from 'react';
import { ToolResultView } from '../../../ToolExecution/ToolResultView';
import { getRenderer } from '../../../tool-results/registry';
import type {
  ToolResultBlock,
  ToolUseBlock,
} from '../../../../store/chatStore';
import { safeJsonParse } from '../../../../../../utils/jsonRepairUtils';
import { ErrorBoundary } from '../ErrorBoundary';
import { useDebouncedValue } from '../useDebouncedValue';
import { useToolResultContent } from '../../ToolResultDataContext';

interface MarkdownFindingsProps {
  code: string;
}

interface FindingsToolResultContent {
  data: Record<string, unknown>[];
  meta?: {
    page?: number;
    perPage?: number;
    total?: number;
    hasNextPage?: boolean;
    hasPreviousPage?: boolean;
  };
}

const MARKDOWN_FINDINGS_TOOL_USE_ID = 'markdown-findings-tool-use';

const Skeleton = () => {
  return (
    <div className="my-4 overflow-hidden rounded-xl bg-white p-6 shadow-sm ring-1 ring-gray-900/5 transition-all duration-200 dark:bg-gray-900">
      <div className="animate-pulse space-y-3">
        <div className="h-8 w-1/3 rounded bg-gray-200 dark:bg-gray-700" />
        <div className="h-6 w-full rounded bg-gray-100 dark:bg-gray-800" />
        <div className="h-6 w-11/12 rounded bg-gray-100 dark:bg-gray-800" />
      </div>
    </div>
  );
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function getFindingsToolResultContent(
  value: unknown,
): FindingsToolResultContent | undefined {
  if (!isRecord(value) || !Array.isArray(value.data)) {
    return undefined;
  }

  const content: FindingsToolResultContent = {
    data: value.data as Record<string, unknown>[],
  };

  if (isRecord(value.meta)) {
    content.meta = value.meta as FindingsToolResultContent['meta'];
  }

  return content;
}

function extractFindingsPayload(parsed: unknown): {
  content: FindingsToolResultContent;
} | null {
  if (Array.isArray(parsed)) {
    return {
      content: {
        data: parsed as Record<string, unknown>[],
        meta: {
          page: 1,
          perPage: parsed.length,
          total: parsed.length,
          hasNextPage: false,
          hasPreviousPage: false,
        },
      },
    };
  }

  if (!isRecord(parsed)) {
    return null;
  }

  const directContent = getFindingsToolResultContent(parsed);
  if (directContent) {
    return {
      content: directContent,
    };
  }

  const wrappedContent = getFindingsToolResultContent(parsed.content);
  if (wrappedContent) {
    return {
      content: wrappedContent,
    };
  }

  return null;
}

/**
 * Merge findings data from the fenced code block with the real toolResult.
 *
 * The LLM often writes a simplified payload in the fenced block (omitting
 * resourceTags, asset info, etc.) while the toolResult has the full data.
 * We merge per-item by matching on `id`: the toolResult item provides the
 * base, and the fenced-block item overlays any fields it explicitly carries.
 */
function mergeWithToolResult(
  fencedItems: Record<string, unknown>[],
  toolResultContent: string | undefined,
): Record<string, unknown>[] {
  if (!toolResultContent) {
    return fencedItems;
  }

  const toolParsed = safeJsonParse<unknown>(
    toolResultContent,
    'findings toolResult merge',
  );
  const toolPayload = extractFindingsPayload(toolParsed);
  if (!toolPayload || toolPayload.content.data.length === 0) {
    return fencedItems;
  }

  // Build a lookup from the full toolResult items keyed by id
  const toolItemById = new Map<string, Record<string, unknown>>();
  for (const item of toolPayload.content.data) {
    const id = typeof item.id === 'string' ? item.id : String(item.id ?? '');
    if (id) {
      toolItemById.set(id, item);
    }
  }

  if (toolItemById.size === 0) {
    return fencedItems;
  }

  return fencedItems.map((fenced) => {
    const id =
      typeof fenced.id === 'string' ? fenced.id : String(fenced.id ?? '');
    const toolItem = id ? toolItemById.get(id) : undefined;
    if (!toolItem) {
      return fenced;
    }
    // toolItem provides the base (all fields); fenced overlays on top so any
    // values the LLM explicitly included take precedence.
    return { ...toolItem, ...fenced };
  });
}

const FindingsRenderer = ({
  code,
  toolResultContent,
}: {
  code: string;
  toolResultContent: string | undefined;
}) => {
  const parsed = safeJsonParse<unknown>(code, 'findings markdown');
  const payload = extractFindingsPayload(parsed);

  if (!payload) {
    return <Skeleton />;
  }

  const mergedData = mergeWithToolResult(
    payload.content.data,
    toolResultContent,
  );

  const mergedContent: FindingsToolResultContent = {
    data: mergedData,
    meta: payload.content.meta,
  };

  const toolUse: ToolUseBlock = {
    type: 'toolUse',
    id: MARKDOWN_FINDINGS_TOOL_USE_ID,
    name: 'listFindings',
    input: {},
    meta: {
      humanReadableName: 'List Plerion findings',
      source: 'plerion',
    },
  };

  const result: ToolResultBlock = {
    type: 'toolResult',
    toolUseId: MARKDOWN_FINDINGS_TOOL_USE_ID,
    toolName: 'listFindings',
    content: JSON.stringify(mergedContent),
    isError: false,
  };

  return (
    <div className="not-prose">
      <ToolResultView
        result={result}
        toolUse={toolUse}
        getRenderer={getRenderer}
      />
    </div>
  );
};

export const MarkdownFindings: React.FC<MarkdownFindingsProps> = ({ code }) => {
  const debouncedCode = useDebouncedValue(code, 300, 500);
  const findingsContent = useToolResultContent('listFindings');
  const attackPathsContent = useToolResultContent('listAttackPaths');
  const toolResultContent = findingsContent ?? attackPathsContent;

  return (
    <div className="not-prose mb-4">
      <ErrorBoundary
        onError={(error, errorInfo) => {
          console.error('Findings markdown rendering error:', error, errorInfo);
        }}
        resetKey={debouncedCode}
        fallback={<Skeleton />}
      >
        <FindingsRenderer
          code={debouncedCode}
          toolResultContent={toolResultContent}
        />
      </ErrorBoundary>
    </div>
  );
};

export default MarkdownFindings;
